import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@repo/ui'
import * as Sentry from '@sentry/react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import {
  Building2,
  CreditCard,
  MoreHorizontal,
  Plus,
  ShieldPlus,
  SlidersHorizontal,
  UserPlus,
  Users
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { AdminOrg } from '@/api/sysadmin/orgs'
import { listOrgs } from '@/api/sysadmin/orgs'
import { SalesChannel } from '@/api/user/profile'
import { ActivateOrgPaymentDialog } from '@/components/admin/ActivateOrgPaymentDialog'
import { BulkInviteDialog } from '@/components/admin/BulkInviteDialog'
import { CreateOrgDialog } from '@/components/admin/CreateOrgDialog'
import { InviteOrgUserDialog } from '@/components/admin/InviteOrgUserDialog'
import { ManageFeaturesDialog } from '@/components/admin/ManageFeaturesDialog'
import { PromoteSysAdminDialog } from '@/components/admin/PromoteSysAdminDialog'

export const Route = createFileRoute('/_auth/admin/')({
  loader: async () => ({ orgs: await listOrgs() }),
  component: AdminOrgsPage,
  head: () => ({ meta: [{ title: 'Admin - Snowvault' }] })
})

function formatDate(tsMs: number): string {
  const date = new Date(tsMs)
  if (!Number.isFinite(date.getTime())) return 'unknown'
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function billingLabel(channel: AdminOrg['salesChannel']): string {
  switch (channel) {
    case SalesChannel.Direct:
      return 'Invoice'
    case SalesChannel.Stripe:
      return 'Stripe'
    case SalesChannel.AwsMarketplace:
      return 'AWS Marketplace'
    default:
      return 'Unknown'
  }
}

function AdminOrgsPage() {
  const router = useRouter()
  const { orgs: loadedOrgs } = Route.useLoaderData()
  const loaderUpdatedAt = Route.useMatch({ select: (match) => match.updatedAt })
  const [orgs, setOrgs] = useState(loadedOrgs)
  const synchronizedLoaderUpdatedAt = useRef(loaderUpdatedAt)
  const directRefreshSequence = useRef(0)
  const [showCreate, setShowCreate] = useState(false)
  const [inviteTarget, setInviteTarget] = useState<AdminOrg | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [bulkTarget, setBulkTarget] = useState<AdminOrg | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [promoteOpen, setPromoteOpen] = useState(false)
  const [featuresTarget, setFeaturesTarget] = useState<AdminOrg | null>(null)
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const [paymentTarget, setPaymentTarget] = useState<AdminOrg | null>(null)
  const [paymentOpen, setPaymentOpen] = useState(false)

  useEffect(() => {
    // Loader data can be structurally shared when revalidation returns the same authoritative
    // rows. Synchronize only after TanStack marks a new loader run complete, so unrelated renders
    // do not replace a successful catchable refresh with the previous loader snapshot.
    if (loaderUpdatedAt === synchronizedLoaderUpdatedAt.current) return
    synchronizedLoaderUpdatedAt.current = loaderUpdatedAt
    setOrgs(loadedOrgs)
  }, [loadedOrgs, loaderUpdatedAt])

  async function refresh() {
    const requestSequence = ++directRefreshSequence.current
    const refreshedOrgs = await listOrgs()
    if (requestSequence === directRefreshSequence.current) {
      setOrgs(refreshedOrgs)
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organisations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {orgs.length === 0
              ? 'No organisations on this Snowvault instance yet.'
              : `${orgs.length} organisation${orgs.length === 1 ? '' : 's'} on this Snowvault instance.`}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={() => setPromoteOpen(true)}>
            <ShieldPlus className="size-4" />
            Promote to sysadmin
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="size-4" />
            Create org
          </Button>
        </div>
      </div>

      {orgs.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center">
          <span className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Building2 className="size-5" />
          </span>
          <div className="space-y-1">
            <p className="text-sm font-medium">No organisations yet</p>
            <p className="text-sm text-muted-foreground">Create the first organisation to get started.</p>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="size-4" />
            Create org
          </Button>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[1%]">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs.map((org) => (
                <TableRow key={org.slug}>
                  <TableCell className="font-medium">{org.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{org.slug}</TableCell>
                  <TableCell>
                    {org.enabled ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Disabled
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{billingLabel(org.salesChannel)}</TableCell>
                  <TableCell>
                    {org.paymentActive ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(org.createdOnTsMs)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="size-8" aria-label={`Actions for ${org.name}`}>
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {org.salesChannel === SalesChannel.Direct && !org.paymentActive && (
                          <DropdownMenuItem
                            onClick={() => {
                              setPaymentTarget(org)
                              setPaymentOpen(true)
                            }}
                          >
                            <CreditCard className="size-4" />
                            Activate payment
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setInviteTarget(org)
                            setInviteOpen(true)
                          }}
                        >
                          <UserPlus className="size-4" />
                          Invite user
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setBulkTarget(org)
                            setBulkOpen(true)
                          }}
                        >
                          <Users className="size-4" />
                          Bulk invite
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setFeaturesTarget(org)
                            setFeaturesOpen(true)
                          }}
                        >
                          <SlidersHorizontal className="size-4" />
                          Manage features
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateOrgDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSuccess={async () => {
          setShowCreate(false)
          await refresh()
        }}
      />

      <InviteOrgUserDialog
        orgSlug={inviteTarget?.slug ?? ''}
        orgName={inviteTarget?.name ?? ''}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={async () => {
          setInviteOpen(false)
          await refresh()
        }}
      />

      <BulkInviteDialog
        orgSlug={bulkTarget?.slug ?? ''}
        orgName={bulkTarget?.name ?? ''}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={async () => {
          await refresh()
        }}
      />

      <PromoteSysAdminDialog open={promoteOpen} onClose={() => setPromoteOpen(false)} />

      <ManageFeaturesDialog
        orgSlug={featuresTarget?.slug ?? ''}
        orgName={featuresTarget?.name ?? ''}
        enabledFeatures={featuresTarget?.enabledFeatures ?? []}
        open={featuresOpen}
        onClose={() => {
          setFeaturesOpen(false)
          void refresh().catch((err: unknown) => {
            Sentry.captureException(err, {
              extra: {
                operation: 'refresh organisations after feature management',
                orgSlug: featuresTarget?.slug
              }
            })
          })
        }}
      />

      <ActivateOrgPaymentDialog
        orgSlug={paymentTarget?.slug ?? ''}
        orgName={paymentTarget?.name ?? ''}
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        onSuccess={async () => {
          const currentOrgSlug = router.options.context.user.userOrgProfile?.org.slug
          if (currentOrgSlug && paymentTarget?.slug === currentOrgSlug) {
            router.update({
              context: {
                ...router.options.context,
                user: { isAuthenticated: false }
              }
            })
            await router.navigate({
              to: '/login',
              search: { notice: 'payment-activated' }
            })
            return
          }

          await refresh()
          setPaymentOpen(false)
        }}
      />
    </div>
  )
}
